/**
 * User Storage Utility
 * Handles multi-account storage in localStorage with tab-specific active session tracking
 * 
 * Storage Strategy:
 * - User data stored in localStorage with user-specific keys: `user_${userName}`
 * - Active user tracked per tab in sessionStorage: `activeUser`
 * - This allows multiple accounts to coexist and new tabs to inherit the active session
 */

/**
 * Generate a unique storage key for a user
 * @param {Object} userData - User data object containing userName
 * @returns {string} Storage key
 */
export const getUserStorageKey = (userData) => {
  const userName = userData?.userData?.userName || userData?.userName || '';
  // For SuperAdmin, we'll use userName only (since there's no compID)
  // But add a prefix to distinguish from BackOffice keys if needed
  return `sa_user_${userName}`;
};

/**
 * Get the active user key from sessionStorage
 * @returns {string|null} Active user storage key
 */
export const getActiveUserKey = () => {
  return sessionStorage.getItem('activeUser');
};

/**
 * Set the active user key in sessionStorage (tab-specific)
 * @param {string} userKey - User storage key
 */
export const setActiveUserKey = (userKey) => {
  sessionStorage.setItem('activeUser', userKey);
};

/**
 * Clear the active user key from sessionStorage
 */
export const clearActiveUserKey = () => {
  sessionStorage.removeItem('activeUser');
};

/**
 * Get user data from localStorage by key
 * @param {string} userKey - User storage key (optional, defaults to active user)
 * @returns {Object|null} User data
 */
export const getUserFromStorage = (userKey = null) => {
  const key = userKey || getActiveUserKey();
  if (!key) return null;

  try {
    const userData = localStorage.getItem(key);
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error parsing user data from storage:', error);
    return null;
  }
};

/**
 * Save user data to localStorage with user-specific key
 * Also sets this user as active in current tab
 * @param {Object} userData - User data to save
 */
export const saveUserToStorage = (userData) => {
  const userKey = getUserStorageKey(userData);
  
  try {
    // Add lastActive timestamp to track most recent login
    const userDataWithTimestamp = {
      ...userData,
      lastActive: Date.now()
    };
    
    localStorage.setItem(userKey, JSON.stringify(userDataWithTimestamp));
    setActiveUserKey(userKey); // Set as active in current tab
    
    // Also store refreshToken with user-specific key if it exists
    if (userData.refreshToken) {
      localStorage.setItem(`${userKey}_refreshToken`, userData.refreshToken);
    }
  } catch (error) {
    console.error('Error saving user data to storage:', error);
  }
};

/**
 * Remove user data from localStorage
 * @param {string} userKey - User storage key (optional, defaults to active user)
 */
export const removeUserFromStorage = (userKey = null) => {
  const key = userKey || getActiveUserKey();
  if (!key) return;

  try {
    localStorage.removeItem(key);
    localStorage.removeItem(`${key}_refreshToken`);
    
    // If removing active user, clear active user key
    if (key === getActiveUserKey()) {
      clearActiveUserKey();
    }
  } catch (error) {
    console.error('Error removing user data from storage:', error);
  }
};

/**
 * Get refresh token for a user
 * @param {string} userKey - User storage key (optional, defaults to active user)
 * @returns {string|null} Refresh token
 */
export const getRefreshToken = (userKey = null) => {
  const key = userKey || getActiveUserKey();
  if (!key) return null;

  try {
    return localStorage.getItem(`${key}_refreshToken`);
  } catch (error) {
    return null;
  }
};

/**
 * Save refresh token for a user
 * @param {string} refreshToken - Refresh token
 * @param {string} userKey - User storage key (optional, defaults to active user)
 */
export const saveRefreshToken = (refreshToken, userKey = null) => {
  const key = userKey || getActiveUserKey();
  if (!key) return;

  try {
    localStorage.setItem(`${key}_refreshToken`, refreshToken);
  } catch (error) {
    console.error('Error saving refresh token:', error);
  }
};

/**
 * Get all logged-in users from localStorage
 * @returns {Array} Array of {key, userData} objects
 */
export const getAllLoggedInUsers = () => {
  const users = [];
  try {
    // Check all localStorage keys that start with 'sa_user_'
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sa_user_') && !key.includes('_refreshToken')) {
        try {
          const userData = JSON.parse(localStorage.getItem(key));
          if (userData && userData.token && userData.userData) {
            users.push({ key, userData, lastActive: userData.lastActive || 0 });
          }
        } catch (e) {
          // Skip invalid entries
        }
      }
    }
    // Sort by lastActive (most recent first)
    users.sort((a, b) => (b.lastActive || 0) - (a.lastActive || 0));
  } catch (error) {
    console.error('Error getting all logged-in users:', error);
  }
  return users;
};

/**
 * Get current user data (active user in current tab)
 * If no active user in sessionStorage, try to restore from localStorage
 * @returns {Object|null} Current user data
 */
export const getCurrentUser = () => {
  // First, try to get from sessionStorage (current tab's active user)
  const activeUserKey = getActiveUserKey();
  if (activeUserKey) {
    const user = getUserFromStorage(activeUserKey);
    if (user && user.token) {
      return user;
    }
  }

  // If no active user in this tab, check localStorage for available users
  // This handles the case when a new tab is opened
  const allUsers = getAllLoggedInUsers();
  
  if (allUsers.length === 0) {
    return null;
  }

  // If only one user is logged in, automatically use that
  // If multiple users, use the most recently active one
  const mostRecentUser = allUsers[0];
  if (mostRecentUser && mostRecentUser.userData) {
    // Set this as active for this tab
    setActiveUserKey(mostRecentUser.key);
    return mostRecentUser.userData;
  }

  return null;
};

/**
 * Check if a user is logged in (has active session in current tab)
 * @returns {boolean} True if user is logged in
 */
export const isUserLoggedIn = () => {
  const user = getCurrentUser();
  return user && user.token ? true : false;
};
